import React from 'react';

import clsx from 'classnames';
import PropTypes from 'prop-types';

import {
    capitalize,
    combineClasses,
} from 'wanderu-css';

import ButtonBase from '../ButtonBase/ButtonBase';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './Button.scss';

function Button(props) {
    const {
        classes: classesProp = {},
        className,
        color = 'primary',
        component: Component = 'button',
        componentProps,
        disabled = false,
        fullWidth = false,
        size = 'large',
        type = 'button',
        variant = 'contained',
        ...otherProps
    } = props;

    const classes = combineClasses(styles, classesProp);

    return (
        <ButtonBase
            className={clsx(
                classes.root,
                classes[variant],
                {
                    [classes.disabled]: disabled,
                    [classes.fullWidth]: fullWidth,
                    [classes[`size${capitalize(size)}`]]: size !== 'large',
                    [classes[`color${capitalize(color)}`]]: color !== 'primary',
                },
                className,
            )}
            component={Component}
            disabled={disabled}
            type={type}
            {...otherProps}
            {...componentProps}
        />
    );
}

Button.propTypes = {
    /**
     * The content of the button.
     */
    children: PropTypes.node,
    /**
     * Override or extend the styles applied to the component.
     */
    classes: PropTypes.object,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * The color of the component. It supports those theme colors that make sense for this component.
     */
    color: PropTypes.oneOf(['primary', 'secondary', 'neutral']),
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component: PropTypes.elementType,
    /**
     * The set of components passed down to the root component
     */
    componentProps: PropTypes.object,
    /**
     * If `true`, the button will be disabled.
     */
    disabled: PropTypes.bool,
    /**
     * If true, the button will take up the full width of its container.
     */
    fullWidth: PropTypes.bool,
    /**
     * The URL to link to when the button is clicked.
     * If defined, an `a` element will be used as the root node.
     */
    href: PropTypes.string,
    /**
     * @ignore
     */
    onClick: PropTypes.func,
    /**
     * The size of the button.
    */
    size: PropTypes.oneOf(['small', 'large']),
    /**
     * Used to control the button's purpose.
     * This prop passes the value to the `type` attribute of the native button component.
     */
    type: PropTypes.oneOf(['submit', 'reset', 'button']),
    /**
     * Used to control the buttons styling.
     * Ghost (Default) - no visible filled box unless hovered.
     * Contained - visible filled box always present.
     * Outlined - visible outlined box always present.
     */
    variant: PropTypes.oneOf(['text', 'ghost', 'outlined', 'contained', 'unstyled']),
};

export default Button;
